import { useReveal } from "./useReveal";

const AGENCY = [
  { label: "Initial consultation", val: "Free*", free: true },
  { label: "Proposal & scoping doc", val: "Free*", free: true },
  { label: "Deposit 50%", val: "$3,500" },
  { label: "Kickoff meeting (2hr)", val: "$4,200" },
  { label: "Discovery workshop", val: "$5,500" },
  { label: "Brand strategy deck", val: "$6,000" },
  { label: "Wireframes review call", val: "$1,500" },
  { label: "Revision round 1", val: "$200" },
  { label: "Project management fee", val: "$300" },
  { label: "Revision round 2", val: "$200" },
  { label: "Final balance 50%", val: "$6,750" },
  { label: "Post-launch fixes", val: "$300" },
  { label: "Monthly retainer", val: "$400/mo" },
];

export function Audit() {
  const { ref, shown } = useReveal<HTMLDivElement>();
  return (
    <section id="audit" className="relative hairline-t py-28 md:py-36">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 40%, oklch(0.7 0.14 70 / 0.07), transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-[1180px] px-6 md:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-[clamp(40px,6.5vw,84px)] leading-[0.95] tracking-[0.02em] text-bone">
            MOST IDEAS DIE FROM{" "}
            <span className="text-gradient-amber italic not-italic">PLANNING THEATRE</span>
          </h2>
          <p className="font-body mx-auto mt-5 max-w-[560px] text-[16px] font-light leading-[1.7] text-[var(--dim-2)]">
            Traditional agencies don't sell results. They sell{" "}
            <em className="text-bone not-italic">process</em>.
          </p>
        </div>

        <div
          ref={ref}
          className={`reveal mt-16 grid gap-px overflow-hidden bg-[var(--hairline)] md:grid-cols-2 ${shown ? "in" : ""}`}
        >
          {/* Agency panel */}
          <div className="flex flex-col bg-[var(--surface)] p-7 md:p-8">
            <div className="mb-6 flex items-center justify-between">
              <div className="font-display text-[12px] tracking-[0.28em] text-[var(--dim-2)]">
                BIG-BOX AGENCY · STANDARD ENGAGEMENT
              </div>
              <span className="border border-[oklch(0.6_0.21_25/0.3)] bg-[oklch(0.6_0.21_25/0.08)] px-2 py-0.5 text-[9px] tracking-[0.12em] text-[var(--blood)]">
                45+ DAYS
              </span>
            </div>

            <ul className="mb-5">
              {AGENCY.map((row) => (
                <li
                  key={row.label}
                  className="flex items-baseline justify-between border-b border-white/[0.03] py-2 text-[11px]"
                >
                  <span className="text-[var(--dim-2)]">{row.label}</span>
                  <span
                    className={`font-mono-tech tabular-nums ${row.free ? "text-[var(--dim)]" : "text-[var(--blood)]"}`}
                  >
                    {row.val}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-auto flex items-end justify-between border-t border-[oklch(0.6_0.21_25/0.3)] pt-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--dim-2)]">
                  Total capital burn
                </div>
                <div className="mt-1 text-[9px] leading-[1.6] text-[var(--dim-2)]">
                  45+ days. Meetings about meetings.
                </div>
              </div>
              <span className="font-display text-[clamp(44px,5.5vw,64px)] leading-none text-[var(--blood)]">
                -$28,850
              </span>
            </div>
          </div>

          {/* OMNIFIC panel */}
          <div className="relative flex flex-col bg-[var(--surface-2)] p-7 md:p-8 outline outline-1 outline-[oklch(0.7_0.14_70/0.32)]">
            <div className="scanlines" />
            <div className="relative flex flex-1 flex-col">
              <div className="mb-6 flex items-center justify-between">
                <div className="font-display text-[12px] tracking-[0.28em] text-[var(--amber-bright)]">
                  OMNIFIC · DIRECT ENGAGEMENT
                </div>
                <span className="border border-[var(--amber)]/40 bg-[oklch(0.7_0.14_70/0.08)] px-2 py-0.5 text-[9px] tracking-[0.12em] text-[var(--amber)]">
                  DAY ONE
                </span>
              </div>

              <div className="clip-corner mb-7 border border-[var(--hairline)] bg-black/30 p-6 text-center">
                <div className="text-[9px] uppercase tracking-[0.32em] text-[var(--dim-2)]">
                  Starting at
                </div>
                <div className="mt-3 flex items-baseline justify-center gap-3">
                  <span className="font-display text-[clamp(64px,8.5vw,98px)] leading-[0.85] text-gradient-amber">
                    $1,888
                  </span>
                </div>
              </div>

              <ul className="mb-6 space-y-2 text-[12px]">
                {[
                  ["Concept → live", "12 hours"],
                  ["Kickoff meeting", "Free"],
                  ["Revisions", "Same-day"],
                  ["Payment", "Any rail"],
                  ["Meetings about meetings", "Zero"],
                  ["Retainer", "Optional. Never required."],
                ].map(([k, v]) => (
                  <li
                    key={k}
                    className="flex items-baseline justify-between border-b border-white/[0.03] py-2"
                  >
                    <span className="text-[var(--dim-2)]">{k}</span>
                    <span className="font-mono-tech text-[var(--signal)] tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto flex items-end justify-between border-t border-[var(--amber)]/30 pt-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--amber-bright)]">
                    Capital reclaimed
                  </div>
                  <div className="mt-1 text-[9px] leading-[1.6] text-[var(--dim-2)]">
                    Live in 12 hours. No meetings about meetings.
                  </div>
                </div>
                <span className="font-display text-[clamp(44px,5.5vw,64px)] leading-none text-[var(--signal)]">
                  +$26,970
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
